import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { getEmbedUrl, getStrategySession, type EmbedParams, type DashboardType } from '../services/api'
import MetabaseEmbed from '../components/MetabaseEmbed'
import KillSignalBoard from '../components/KillSignalBoard'
import LeadershipTabs, { type DashboardTab } from '../components/LeadershipTabs'
import rhythmicLogo from '../assets/rhythmic-logo-white.svg'

const DEFAULT_TAB: DashboardTab = 'leadership-pulse'
type TimeframePeriod = '1' | '3' | '12'
type ComparisonPeriod = 'mom' | 'qoq' | 'yoy'
type BudgetVersion = 'budget' | 'reforecast'
type ScorecardDept = 'finance' | 'leadership' | 'operations' | 'ps' | 'sales'

const SCORECARD_DASHBOARD_MAP: Record<ScorecardDept, DashboardType> = {
  'finance': 'scorecard-finance',
  'leadership': 'scorecard-leadership',
  'operations': 'scorecard-operations',
  'ps': 'scorecard-ps',
  'sales': 'scorecard-sales',
}

function LeadershipDashboard() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user, groups, signOut } = useAuth()
  const isAdmin = groups.includes('admin')

  // Get active tab from URL or default
  const tabParam = searchParams.get('tab') as DashboardTab | null
  const activeTab: DashboardTab = tabParam && isValidTab(tabParam) ? tabParam : DEFAULT_TAB

  // Timeframe selector for sales dashboard (default 3 months)
  const [timeframe, setTimeframe] = useState<TimeframePeriod>('3')

  // Period Comparison selectors
  const [comparison, setComparison] = useState<ComparisonPeriod>('yoy')
  const [budgetVersion, setBudgetVersion] = useState<BudgetVersion>('budget')

  // KPI Scorecard department selector
  const [scorecardDept, setScorecardDept] = useState<ScorecardDept>('finance')

  const [embedUrl, setEmbedUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  function isValidTab(tab: string): tab is DashboardTab {
    return [
      'leadership-pulse',
      'leadership-revenue',
      'leadership-margins',
      'leadership-cash',
      'leadership-compare',
      'leadership-sales',
      'leadership-people',
      'leadership-marketing',
      'leadership-scorecard',
      'kill-signals',
    ].includes(tab)
  }

  useEffect(() => {
    // Kill Signals renders from our own API, not a Metabase embed.
    if (activeTab === 'kill-signals') {
      setEmbedUrl(null)
      setError(null)
      setIsLoading(false)
      return
    }

    async function fetchEmbedUrl() {
      setIsLoading(true)
      setError(null)
      try {
        // Pass params based on active dashboard
        let params: EmbedParams | undefined
        let dashboardKey: DashboardType = activeTab as DashboardType
        if (activeTab === 'leadership-sales') {
          params = { timeframe }
        } else if (activeTab === 'leadership-compare') {
          params = { comparison, budget_cat: budgetVersion }
        } else if (activeTab === 'leadership-scorecard') {
          dashboardKey = SCORECARD_DASHBOARD_MAP[scorecardDept]
        }
        const url = await getEmbedUrl(dashboardKey, params)
        setEmbedUrl(url)
      } catch (err) {
        setError('Failed to load dashboard. Please try again.')
        console.error('Failed to get embed URL:', err)
      } finally {
        setIsLoading(false)
      }
    }

    fetchEmbedUrl()
  }, [activeTab, timeframe, comparison, budgetVersion, scorecardDept])

  function handleTabChange(tab: DashboardTab) {
    setSearchParams({ tab })
  }

  function handleViewCompany() {
    navigate('/company')
  }

  async function handleOpenStrategy() {
    try {
      const session = await getStrategySession()
      window.location.assign(session.url)
    } catch (err) {
      setError('Failed to open Strategy. Please try again.')
      console.error('Failed to get strategy session:', err)
    }
  }

  return (
    <>
      <header className="header">
        <div className="header-brand">
          <img src={rhythmicLogo} alt="Rhythmic" className="header-logo" />
          <span className="header-title">Dashboard</span>
        </div>
        <div className="header-right">
          <span>{user?.email}</span>
          <span className="badge badge-leadership">Leadership</span>
          <button className="btn btn-secondary" onClick={handleOpenStrategy}>
            Strategy
          </button>
          {isAdmin && (
            <button className="btn btn-secondary" onClick={() => navigate('/admin/users')}>
              Admin
            </button>
          )}
          <button className="btn btn-secondary" onClick={signOut}>
            Sign out
          </button>
        </div>
      </header>

      <LeadershipTabs
        activeTab={activeTab}
        onTabChange={handleTabChange}
        onViewCompany={handleViewCompany}
      />

      {activeTab === 'leadership-sales' && (
        <div className="filter-bar">
          <label htmlFor="timeframe-select">Trend Comparison:</label>
          <select
            id="timeframe-select"
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value as TimeframePeriod)}
            className="filter-select"
          >
            <option value="1">vs Last Month</option>
            <option value="3">vs 3 Months Ago</option>
            <option value="12">vs Last Year</option>
          </select>
        </div>
      )}

      {activeTab === 'leadership-scorecard' && (
        <div className="filter-bar">
          <label htmlFor="dept-select">Department:</label>
          <select
            id="dept-select"
            value={scorecardDept}
            onChange={(e) => setScorecardDept(e.target.value as ScorecardDept)}
            className="filter-select"
          >
            <option value="finance">Finance & Admin</option>
            <option value="leadership">Leadership</option>
            <option value="operations">Operations</option>
            <option value="ps">Professional Services</option>
            <option value="sales">Sales</option>
          </select>
        </div>
      )}

      {activeTab === 'leadership-compare' && (
        <div className="filter-bar">
          <label htmlFor="comparison-select">Compare To:</label>
          <select
            id="comparison-select"
            value={comparison}
            onChange={(e) => setComparison(e.target.value as ComparisonPeriod)}
            className="filter-select"
          >
            <option value="mom">Prior Month</option>
            <option value="qoq">Prior Quarter</option>
            <option value="yoy">Prior Year</option>
          </select>

          <label htmlFor="budget-select" style={{ marginLeft: '1rem' }}>Budget Version:</label>
          <select
            id="budget-select"
            value={budgetVersion}
            onChange={(e) => setBudgetVersion(e.target.value as BudgetVersion)}
            className="filter-select"
          >
            <option value="budget">Baseline Budget</option>
            <option value="reforecast">Latest Reforecast</option>
          </select>
        </div>
      )}

      <main className="main main-with-tabs">
        {activeTab === 'kill-signals' ? (
          <KillSignalBoard />
        ) : error ? (
          <div className="error">{error}</div>
        ) : isLoading ? (
          <div className="loading">Loading dashboard...</div>
        ) : embedUrl ? (
          <MetabaseEmbed url={embedUrl} />
        ) : null}
      </main>
    </>
  )
}

export default LeadershipDashboard
